import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import { useReference } from '../hooks/useReference'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Select } from '../components/controls'
import {
  PaymentMethodForm,
  emptyPaymentMethodForm,
  type PaymentMethodFormValue,
} from '../components/paymentMethodForm'

export interface PaymentMethod {
  paymentMethodId: number
  paymentMethodTypeId: number
  paymentMethodType: string
  paymentMethod: string
  description: string
  logoPath: string
  icon: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
  countryCount: number
  causalCount: number
}

interface PaymentMethodPage {
  paymentMethods: PaymentMethod[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * PaymentMethodsPage is the PSP hub — [PaymentMethodHub].[PaymentMethod] in the
 * BsConnekto database.
 *
 * A method's real configuration is its two link tables (where it may be used,
 * and which wallet causals back its deposits and withdrawals), so the list shows
 * both counts and links into the detail page. Retiring a method keeps its
 * links, so switching it back on restores the whole configuration.
 */
export function PaymentMethodsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { paymentMethodTypes } = useReference()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<PaymentMethod | null>(null)
  const [form, setForm] = useState<PaymentMethodFormValue>(emptyPaymentMethodForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(typeFilter ? { paymentMethodTypeId: typeFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['payment-methods', params],
    queryFn: async (): Promise<PaymentMethodPage> => {
      const res = await api.get('/payment-methods', { params })
      return res.data as PaymentMethodPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['payment-methods'] })

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyPaymentMethodForm, paymentMethodTypeId: String(paymentMethodTypes[0]?.id ?? '') })
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (m: PaymentMethod) => {
    setEditing(m)
    setForm({
      paymentMethodTypeId: String(m.paymentMethodTypeId),
      paymentMethod: m.paymentMethod,
      description: m.description,
      logoPath: m.logoPath,
      icon: m.icon,
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        paymentMethodTypeId: Number(form.paymentMethodTypeId),
        paymentMethod: form.paymentMethod.trim(),
        description: form.description.trim(),
        logoPath: form.logoPath.trim(),
        icon: form.icon.trim(),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/payment-methods/${editing.paymentMethodId}`, body)
        : api.post('/payment-methods', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('paymentMethods.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (m: PaymentMethod) =>
      m.isDisabledByPlatform
        ? api.post(`/payment-methods/${m.paymentMethodId}/restore`)
        : api.delete(`/payment-methods/${m.paymentMethodId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('paymentMethods.errors.save'))),
  })

  const methods = data?.paymentMethods ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('paymentMethods.title')}
        subtitle={t('paymentMethods.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate} disabled={paymentMethodTypes.length === 0}>
                    <Plus />
                    {t('paymentMethods.newPaymentMethod')}
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editing
                      ? t('paymentMethods.editPaymentMethod')
                      : t('paymentMethods.newPaymentMethod')}
                  </DialogTitle>
                </DialogHeader>
                <PaymentMethodForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  types={paymentMethodTypes}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="payment-methods">
        <p>{t('paymentMethods.help.line1')}</p>
        <p>{t('paymentMethods.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('paymentMethods.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || typeFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setTypeFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v)
            setOffset(0)
          }}
          items={paymentMethodTypes.map((pt) => ({ value: String(pt.id), label: pt.name }))}
          placeholder={t('paymentMethods.allTypes')}
          className="w-44"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('paymentMethods.columns.id')}</TableHead>
                  <TableHead>{t('paymentMethods.columns.name')}</TableHead>
                  <TableHead className="w-36">{t('paymentMethods.columns.type')}</TableHead>
                  <TableHead className="w-28">{t('paymentMethods.columns.countries')}</TableHead>
                  <TableHead className="w-28">{t('paymentMethods.columns.causals')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {methods.map((m) => (
                  <TableRow key={m.paymentMethodId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {m.paymentMethodId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to={`/payment-methods/${m.paymentMethodId}`}
                        className="text-primary underline underline-offset-2"
                      >
                        {m.paymentMethod}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{m.paymentMethodType}</TableCell>
                    <TableCell>
                      <Link
                        to={`/payment-methods/${m.paymentMethodId}?tab=countries`}
                        className="text-primary underline underline-offset-2"
                      >
                        {m.countryCount}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/payment-methods/${m.paymentMethodId}?tab=causals`}
                        className="text-primary underline underline-offset-2"
                      >
                        {m.causalCount}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={m.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(m)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={m.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(m)}
                          >
                            {m.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {methods.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 7 : 6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('paymentMethods.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('paymentMethods.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
